import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import Utils from 'src/app/helpers/utils';
import { ApiService } from 'src/app/services/api/api.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import {
  BreadcrumbService,
  BreadcrumbItem,
} from 'src/app/services/breadcrumb/breadcrumb.service';

@Component({
  selector: 'app-dashboard-drafts-versions-form',
  templateUrl: './dashboard-drafts-versions-form.component.html',
  styleUrls: ['./dashboard-drafts-versions-form.component.scss'],
})
export class DashboardDraftsVersionsFormComponent implements OnInit {
  form = this.formBuilder.group({
    description: [null, []],
    type_document: [null, [Validators.required]],
    event: [null, [Validators.required]],
    system: [null, [Validators.required]],
  });

  id = null;

  eventOptions: SelectItem[] = [
    { label: 'Termo de Cessão', value: 'assignment' },
    { label: 'Contrato de Cessão', value: 'transfer' },
    { label: 'Duplicatas', value: 'duplicates' },
  ];

  loading = true;
  title = '';
  sending = false;
  showForm = true;

  constructor(
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private router: Router,
    private api: ApiService,
    private toast: ToastService,
    private authService: AuthService,
    private breadcrumbService: BreadcrumbService,
  ) {}

  ngOnInit(): void {
    if (
      (this.router.url.includes('new') &&
        !this.authService.verifyPermission(['de_can_add_types'])) ||
      (this.route.snapshot.params.id &&
        !this.authService.verifyPermission(['de_can_edit_types']))
    ) {
      this.toast.show(
        'error',
        'Aviso',
        'Você não tem permissão para acessar essa página.',
      );
      this.router.navigateByUrl('/app/dashboard');
    }

    this.form.valueChanges.subscribe((values) => {
      Utils.getErrors(this.form);
    });

    this.route.params.subscribe(({ id }) => {
      if (!this.router.url.includes('new')) {
        this.id = id;
        this.setData();
      } else {
        this.id = null;
      }

      this.loading = false;
    });

    this.setBreadcrumb();
  }

  async setData() {
    try {
      const { data } = await this.api.get({
        route: `api/document_type_minutes/${this.id}/`,
        token: true,
      });

      this.form.patchValue({
        ...data,
        system: data.system === 'data_engine' ? 'data_engine' : 'fs_engine',
      });

      this.title = data.type_document;
      // Atualizar breadcrumb após carregar os dados (muda de "Novo tipo" para "Editar tipo")
      this.setBreadcrumb();
    } catch (error) {
      console.error(error);
    }
  }

  redirect(url: string) {
    this.router.navigate([url]);
  }

  getDisabled() {
    return !this.form.valid;
  }

  async handleSubmit() {
    if (this.showForm) {
      this.showForm = false;
      return;
    }

    this.sending = true;

    try {
      const payload = { ...this.form.value, status: true };

      let response = null;

      if (this.id) {
        response = await this.api.patch({
          route: `api/document_type_minutes/${this.id}/`,
          token: true,
          body: payload,
        });
      } else {
        response = await this.api.post({
          route: `api/document_type_minutes/`,
          token: true,
          body: payload,
        });
      }

      this.toast.show(
        'info',
        'Sucesso',
        this.id
          ? 'Tipo de documento atualizado, já está disponível para ser utilizado pelo sistema'
          : 'Um novo tipo de documento foi criado e já está disponível para ser utilizado pelo sistema',
      );

      this.router.navigate(['/app/drafts/types']);
    } catch (error) {
      console.error(error);
      this.toast.show(
        'error',
        'Erro',
        'Ocorreu um erro ao salvar o tipo de documento.',
      );
    }
    this.sending = false;
  }

  verifyChecked(field: string, value: string) {
    return this.form.get(field).value === value;
  }

  renderButtonText() {
    if (this.showForm) {
      return 'Avançar';
    } else {
      return 'Enviar para aprovação';
    }
  }

  renderEvent() {
    const event = this.form.controls.event.value;

    if (event) {
      const foundEvent = this.eventOptions.find((item) => item.value === event);
      return foundEvent ? foundEvent.label : 'Evento não encontrado';
    } else {
      return 'Selecione um evento';
    }
  }

  renderSystem() {
    const system = this.form.controls.system.value;

    if (system === 'fs_engine') {
      return 'Smart FIDC';
    } else {
      return 'Cadastro';
    }
  }

  renderActualDateTime() {
    const date = new Date();
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    };
    return date.toLocaleString('pt-BR', options);
  }

  renderUserName() {
    const user = this.authService.getUser();

    return user.active_register.register.name;
  }

  setBreadcrumb(): void {
    const canViewDrafts =
      this.authService.verifyPermission('de_can_view_drafts');
    const breadcrumbs: BreadcrumbItem[] = [
      {
        label: 'Minutas',
        url: '/app/drafts',
        clickable: canViewDrafts,
      },
      {
        label: 'Tipos',
        url: '/app/drafts/types',
        clickable: canViewDrafts, // Clique habilitado quando estiver na página de novo/editar
      },
      {
        label: this.id ? 'Editar tipo' : 'Novo tipo',
        url: this.router.url,
        clickable: false, // Página atual, não precisa ser clicável
      },
    ];
    this.breadcrumbService.setBreadcrumbs(breadcrumbs);
  }
}
